/**
 * «Звонки»: что выбрал человек → как спросить CRM, и как показать ответ.
 * Повторяет веб-CRM (GiperCRM/client/src/lib/callsFilters.ts): период считается здесь, по часам телефона,
 * сервер получает готовые границы [from, to).
 *
 * «МОИ» И ВКЛАДКА ВМЕСТЕ. С 11.10.2026 CRM принимает отбор mine=1 рядом с любой вкладкой — и в журнале,
 * и в сводке (минуты, пропущенные — только мои), а в ответе говорит `mine: true`. Поэтому:
 *   • у менеджера (область own) сервер и так отдаёт только его звонки и звонки его сделок — шлём вкладку как есть;
 *   • у руководителя в режиме «Мои» шлём вкладку + mine=1;
 *   • старый сервер mine=1 не знает (в ответе нет `mine`) — тогда прежний путь `legacy`: tab=mine, а вид
 *     (входящие/пропущенные…) отбираем на телефоне, минуты «моими» не режутся.
 */
import type { CallItem, CallsScopeKind } from '@/api/calls';

export type CallKind = 'all' | 'incoming' | 'outgoing' | 'missed' | 'unreached';
export type CallPeriod = 'today' | 'yesterday' | 'week' | 'month';

export const CALL_KINDS: Array<{ key: CallKind; label: string }> = [
  { key: 'all', label: 'Все' },
  { key: 'incoming', label: 'Входящие' },
  { key: 'outgoing', label: 'Исходящие' },
  { key: 'missed', label: 'Пропущенные' },
  { key: 'unreached', label: 'Недозвоны' },
];

export const CALL_PERIODS: Array<{ key: CallPeriod; label: string }> = [
  { key: 'today', label: 'Сегодня' },
  { key: 'yesterday', label: 'Вчера' },
  { key: 'week', label: '7 дней' },
  { key: 'month', label: '30 дней' },
];

export const periodLabel = (p: CallPeriod) => CALL_PERIODS.find((x) => x.key === p)?.label ?? '';

export interface CallsView {
  kind: CallKind;
  period: CallPeriod;
  /** «Мои звонки» (иначе — все звонки команды, только если область позволяет). */
  mine: boolean;
}

/** Страница журнала — столько строк за запрос (у сервера по умолчанию 50, предел 200). */
export const CALLS_PAGE = 40;

const startOfDay = (d: Date) => new Date(d.getFullYear(), d.getMonth(), d.getDate());
const addDays = (d: Date, n: number) => new Date(d.getFullYear(), d.getMonth(), d.getDate() + n);

/** Границы периода по местным часам: [from, to). Сдвиг по календарным дням — переход на летнее время не ломает. */
export function periodRange(period: CallPeriod, now = new Date()): { from: Date; to: Date } {
  const today = startOfDay(now);
  switch (period) {
    case 'today': return { from: today, to: addDays(today, 1) };
    case 'yesterday': return { from: addDays(today, -1), to: today };
    case 'week': return { from: addDays(today, -6), to: addDays(today, 1) };
    case 'month': return { from: addDays(today, -29), to: addDays(today, 1) };
  }
}

/** Нужен ли режим «мои + вид на телефоне». Область ещё не известна — считаем, что нужен (так верно при любой области). */
export function mineOnPhone(v: CallsView, scope: CallsScopeKind | null): boolean {
  return v.mine && scope !== 'own';
}

export interface CallsRequest {
  /** Параметры журнала (без курсора). */
  list: Record<string, string>;
  /** Параметры сводки (без вкладки). */
  summary: Record<string, string>;
  /**
   * Запасной путь для старого сервера (режим «мои» у руководителя): журнал по tab=mine и вид, который
   * отбирается на телефоне. null — режим «мои» сервером не нужен.
   */
  legacy: { list: Record<string, string>; local: CallKind | null } | null;
}

/** Выбор человека → запросы к CRM. */
export function callsRequest(v: CallsView, scope: CallsScopeKind | null, now = new Date()): CallsRequest {
  const { from, to } = periodRange(v.period, now);
  const range = { from: from.toISOString(), to: to.toISOString() };
  const list: Record<string, string> = { ...range, limit: String(CALLS_PAGE) };
  const summary: Record<string, string> = { ...range };
  if (v.kind !== 'all') list.tab = v.kind;
  if (!mineOnPhone(v, scope)) return { list, summary, legacy: null };
  list.mine = '1';
  summary.mine = '1';
  return {
    list,
    summary,
    legacy: { list: { ...range, limit: String(CALLS_PAGE), tab: 'mine' }, local: v.kind === 'all' ? null : v.kind },
  };
}

/** Ответ сервера применил «мои» сам (новый сервер). Нет поля — старый сервер, отбор проигнорирован. */
export const serverAppliedMine = (r: { mine?: boolean } | undefined) => r?.mine === true;

const noAnswer = (s: string) => s === 'no_answer' || s === 'failed';
export const isMissed = (c: Pick<CallItem, 'direction' | 'status'>) => c.direction === 'incoming' && noAnswer(c.status);
export const isUnreached = (c: Pick<CallItem, 'direction' | 'status'>) => c.direction === 'outgoing' && noAnswer(c.status);
/** Пропущенный, которому ещё не перезвонили. */
export const isMissedOpen = (c: Pick<CallItem, 'direction' | 'status' | 'callback'>) => isMissed(c) && !c.callback?.done;

/** Тот же отбор вида, что у сервера (domain/callsList.ts → tabSql), — для режима «мои» у руководителя. */
export function matchesKind(c: Pick<CallItem, 'direction' | 'status'>, kind: CallKind | null): boolean {
  switch (kind) {
    case null:
    case 'all': return true;
    case 'incoming': return c.direction === 'incoming';
    case 'outgoing': return c.direction === 'outgoing';
    case 'missed': return isMissed(c);
    case 'unreached': return isUnreached(c);
  }
}

/**
 * Пропущенные без перезвона — по клиентам: три звонка одного человека — одно «перезвонить».
 * Ключ — сделка, иначе последние 9 цифр номера, иначе сам звонок.
 */
export function missedOpenCount(items: Array<Pick<CallItem, 'id' | 'direction' | 'status' | 'callback' | 'dealId' | 'phone'>>): number {
  const keys = new Set<string>();
  for (const c of items) {
    if (!isMissedOpen(c)) continue;
    const digits = (c.phone ?? '').replace(/\D/g, '');
    keys.add(c.dealId ? `d:${c.dealId}` : digits.length >= 7 ? `p:${digits.slice(-9)}` : `c:${c.id}`);
  }
  return keys.size;
}

/** Параметры запроса для значка вкладки: мои звонки за 7 дней (пропущенные сверху не отберёшь — «мои» уже вкладка). */
export function badgeRequest(now = new Date()): Record<string, string> {
  const { from, to } = periodRange('week', now);
  return { tab: 'mine', from: from.toISOString(), to: to.toISOString(), limit: '200' };
}

// ── Слова ──

/** Исход словами — с учётом направления (как веб). */
export function outcomeWords(c: Pick<CallItem, 'outcome' | 'direction' | 'status'>): string {
  switch (c.outcome) {
    case 'answered': return 'Разговор';
    case 'busy': return 'Занято';
    case 'no_answer': return c.direction === 'incoming' ? 'Пропущенный' : 'Не ответили';
    case 'failed': return c.direction === 'incoming' ? 'Сорвался' : 'Не дозвонились';
    case 'live': return c.status === 'ringing' ? 'Звонит' : 'Идёт разговор';
  }
}

/** Заголовок окна звонка: «Входящий, разговор 4:12», «Исходящий, не ответили». */
export function callTitle(c: Pick<CallItem, 'outcome' | 'direction' | 'status' | 'durationSeconds'>): string {
  const dir = c.direction === 'incoming' ? 'Входящий' : 'Исходящий';
  if (c.outcome === 'answered') return `${dir}, разговор ${clock(c.durationSeconds)}`;
  return `${dir}, ${outcomeWords(c).toLowerCase()}`;
}

/** 0:42, 12:05, 1:02:10. Нет разговора — тире. */
export function clock(sec: number | null | undefined): string {
  if (!sec || sec <= 0) return '—';
  const h = Math.floor(sec / 3600);
  const m = Math.floor((sec % 3600) / 60);
  const ss = String(Math.floor(sec % 60)).padStart(2, '0');
  return h > 0 ? `${h}:${String(m).padStart(2, '0')}:${ss}` : `${m}:${ss}`;
}

/** Минуты разговоров: «45», «192» — целым числом, как в прототипе. */
export const talkMinutes = (sec: number) => Math.round(sec / 60);

const DAY_FMT = new Intl.DateTimeFormat('ru-RU', { weekday: 'short', day: 'numeric', month: 'long' });
const TIME_FMT = new Intl.DateTimeFormat('ru-RU', { hour: '2-digit', minute: '2-digit' });

export function dayKeyOf(iso: string): string {
  const d = new Date(iso);
  return `${d.getFullYear()}-${d.getMonth() + 1}-${d.getDate()}`;
}

/** «Сегодня», «Вчера», «пт, 9 октября». */
export function callDayLabel(iso: string, now = new Date()): string {
  const d = startOfDay(new Date(iso)).getTime();
  const today = startOfDay(now);
  if (d === today.getTime()) return 'Сегодня';
  if (d === addDays(today, -1).getTime()) return 'Вчера';
  return DAY_FMT.format(new Date(iso));
}

export const timeOf = (iso: string) => TIME_FMT.format(new Date(iso));

/** Звонки по дням — в порядке журнала (свежие сверху). */
export function groupByDay<T extends { occurredAt: string }>(items: T[]): Array<{ key: string; iso: string; items: T[] }> {
  const out: Array<{ key: string; iso: string; items: T[] }> = [];
  for (const it of items) {
    const key = dayKeyOf(it.occurredAt);
    const last = out[out.length - 1];
    if (last && last.key === key) last.items.push(it);
    else out.push({ key, iso: it.occurredAt, items: [it] });
  }
  return out;
}

/** Склейка страниц: одна строка — один звонок (живое обновление могло сдвинуть курсор). */
export function uniqueCalls<T extends { id: string }>(pages: T[][]): T[] {
  const seen = new Set<string>();
  const out: T[] = [];
  for (const p of pages) for (const c of p) if (!seen.has(c.id)) { seen.add(c.id); out.push(c); }
  return out;
}

/** Имя в строке: человек → компания → сделка → номер. */
export function callName(c: Pick<CallItem, 'personName' | 'companyName' | 'dealTitle' | 'phone'>): string {
  return c.personName || c.companyName || c.dealTitle || c.phone || 'Неизвестный номер';
}
